import { useContext } from 'react'
import GlobalContext from '../../../../../context/global-context'
import InventoryHeader from './features/hub/InventoryHeader'
import InventoryHub from './features/hub/InventoryHub'
import CreateItemModal from './features/items/CreateItemModal'
import DeleteItemModal from './features/items/DeleteItemModal'
import EditItemModal from './features/items/EditItemModal'
import ItemsSection from './features/items/ItemsSection'
import MovementsSection from './features/movements/MovementsSection'
import OperationsSection from './features/operations/OperationsSection'
import RecipesSection from './features/recipes/RecipesSection'
import SalesNoteSection from './features/sales-note/SalesNoteSection'
import StockSection from './features/stock/StockSection'
import InventoryUploadModal from './features/uploads/InventoryUploadModal'
import UploadsSection from './features/uploads/UploadsSection'
import DeleteWarehouseModal from './features/warehouses/DeleteWarehouseModal'
import CreateWarehouseModal from './features/warehouses/CreateWarehouseModal'
import WarehousesSection from './features/warehouses/WarehousesSection'
import useInventoryActions from './hooks/useInventoryActions'
import useInventoryData from './hooks/useInventoryData'
import useInventoryNavigation from './hooks/useInventoryNavigation'
import useInventoryPageState from './hooks/useInventoryPageState'

const InventoryPage = () => {
  const { currentBusiness } = useContext(GlobalContext)
  const businessId = Number(currentBusiness?.id || 0)

  const navigation = useInventoryNavigation()
  const state = useInventoryPageState()
  const data = useInventoryData({ businessId, state })
  const actions = useInventoryActions({ businessId, data, navigation, state })
  const hasActiveWorkspace = Boolean(navigation.activeTab)

  if (!businessId) {
    return (
      <div className='bg-white border border-gray-200 rounded-2xl p-6'>
        <p className='text-muted text-sm'>Selecciona una empresa para trabajar el módulo de inventario.</p>
      </div>
    )
  }

  return (
    <div className='space-y-3'>
      <InventoryHub
        activeTab={navigation.activeTab}
        onNavigateToSection={navigation.navigateToSection}
      />

      <InventoryHeader
        activeSection={navigation.activeSection}
        activeTab={navigation.activeTab}
        hubStats={data.hubStats}
        isHubView
        onNavigateHome={() => navigation.navigateToSection()}
        onNavigateToSection={navigation.navigateToSection}
        onRefresh={actions.handleRefreshAll}
      />

      {hasActiveWorkspace ? (
        <>
          {navigation.activeTab === 'warehouses' && (
            <WarehousesSection actions={actions} data={data} state={state} />
          )}

          {navigation.activeTab === 'items' && <ItemsSection actions={actions} data={data} state={state} />}

          {navigation.activeTab === 'operations' && (
            <OperationsSection
              actions={actions}
              data={data}
              navigation={navigation}
              state={state}
            />
          )}

          {navigation.activeTab === 'sales-note' && (
            <SalesNoteSection currentBusiness={currentBusiness} data={data} navigation={navigation} />
          )}

          {navigation.activeTab === 'recipes' && (
            <RecipesSection
              actions={actions}
              businessId={businessId}
              data={data}
              navigation={navigation}
              state={state}
            />
          )}

          {navigation.activeTab === 'uploads' && (
            <UploadsSection actions={actions} data={data} navigation={navigation} state={state} />
          )}

          {navigation.activeTab === 'stock' && <StockSection data={data} state={state} />}

          {navigation.activeTab === 'movements' && <MovementsSection data={data} state={state} />}
        </>
      ) : (
        <section className='rounded-[28px] border border-dashed border-slate-300 bg-white/80 px-5 py-10 text-center shadow-[0_12px_28px_rgba(15,23,42,0.04)]'>
          <div className='mx-auto max-w-2xl'>
            <p className='text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500'>Workspace</p>
            <h3 className='mt-2 text-xl font-semibold text-slate-900'>Selecciona una opción para comenzar</h3>
            <p className='mt-2 text-sm leading-6 text-slate-600'>
              Elige una tarjeta en <span className='font-semibold text-slate-800'>Crear</span> o
              <span className='font-semibold text-slate-800'> Explorar</span> para desplegar la
              tabla, formulario o carga Excel en esta misma pantalla.
            </p>
          </div>
        </section>
      )}

      <DeleteWarehouseModal
        warehouse={state.warehouseToDelete}
        isPending={actions.deleteWarehouseMutation.isPending}
        onClose={actions.handleCloseDeleteWarehouseModal}
        onConfirm={actions.handleConfirmDeleteWarehouse}
      />

      <CreateWarehouseModal
        actions={actions}
        data={data}
        open={state.isCreateWarehouseModalOpen}
        state={state}
      />

      <DeleteItemModal
        item={state.itemToDelete}
        isPending={actions.deleteItemMutation.isPending}
        onClose={actions.handleCloseDeleteItemModal}
        onConfirm={actions.handleConfirmDeleteItem}
      />

      <CreateItemModal
        actions={actions}
        open={state.isCreateItemModalOpen}
        state={state}
      />

      <EditItemModal
        actions={actions}
        open={state.isEditItemModalOpen}
        state={state}
      />

      <InventoryUploadModal
        actions={actions}
        data={data}
        navigation={navigation}
        open={navigation.isUploadModalOpen}
        state={state}
      />
    </div>
  )
}

export default InventoryPage
